// src/paginas/RestablecerPassword.js
// Segundo paso del flujo de "Recuperación de contraseña". Lee `token` y `email` de la URL
// (enlace recibido por correo) y permite establecer una nueva contraseña.
import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../servicios/api';

const RestablecerPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmarPassword, setConfirmarPassword] = useState('');
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('');
  const [completado, setCompletado] = useState(false);

  const enlaceInvalido = !token || !email;

  const manejarEnvio = async (e) => {
    e.preventDefault();
    setMensaje('');

    if (password !== confirmarPassword) {
      setTipoMensaje('error');
      setMensaje('Las contraseñas no coinciden.');
      return;
    }
    if (password.length < 8) {
      setTipoMensaje('error');
      setMensaje('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setEnviando(true);
    try {
      const response = await fetch(`${API_URL}/restablecer_password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, password }),
      });

      const datos = await response.json();
      setTipoMensaje(datos.exito ? 'exito' : 'error');
      setMensaje(datos.mensaje);

      if (datos.exito) {
        setCompletado(true);
        setTimeout(() => navigate('/iniciar-sesion'), 2500);
      }
    } catch (error) {
      console.error('Error al restablecer contraseña:', error);
      setTipoMensaje('error');
      setMensaje('Error de conexión con el servidor. Inténtalo de nuevo más tarde.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-dark-background px-4 py-10">
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-orange/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-sky-500/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-gray-900/80 p-8 shadow-2xl shadow-black/40 backdrop-blur-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange/15 ring-1 ring-brand-orange/30">
            <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-brand-orange" stroke="currentColor" strokeWidth="1.8">
              <rect x="5" y="11" width="14" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
              <circle cx="12" cy="15.5" r="1.5" />
            </svg>
          </div>
          <h3 className="mt-4 text-center text-2xl font-bold tracking-tight text-white">Restablecer contraseña</h3>
          <p className="mt-1 text-center text-sm leading-relaxed text-gray-400">
            {enlaceInvalido
              ? 'Este enlace no es válido.'
              : `Crea una nueva contraseña para ${email}.`}
          </p>

          {mensaje && (
            <div
              role="alert"
              className={`mt-5 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${
                tipoMensaje === 'exito'
                  ? 'border-emerald-800 bg-emerald-950/40 text-emerald-200'
                  : 'border-red-800 bg-red-950/40 text-red-200'
              }`}
            >
              <svg viewBox="0 0 24 24" fill="none" className="mt-0.5 h-4 w-4 flex-shrink-0" stroke="currentColor" strokeWidth="2">
                {tipoMensaje === 'exito' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.86 1.8 18a1 1 0 0 0 .86 1.5h18.68a1 1 0 0 0 .86-1.5L13.7 3.86a1 1 0 0 0-1.7 0Z" />
                )}
              </svg>
              <span>{mensaje}</span>
            </div>
          )}

          {enlaceInvalido && (
            <p className="mt-5 text-center text-sm text-gray-400">
              Faltan datos en el enlace. Solicita uno nuevo desde la pantalla de recuperación.
            </p>
          )}

          {!enlaceInvalido && !completado && (
            <form onSubmit={manejarEnvio} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="password">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <rect x="5" y="11" width="14" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
                    </svg>
                  </span>
                  <input
                    type={mostrarPassword ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-10 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarPassword((valor) => !valor)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 transition hover:text-brand-orange"
                    aria-label={mostrarPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {mostrarPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.4 5.5A10.4 10.4 0 0 1 12 5c5 0 9 4 10 7-.4 1.1-1.1 2.3-2.1 3.4M6.6 6.6C4.6 8 3.4 9.8 3 12c1 3 5 7 9 7 1.3 0 2.5-.3 3.6-.9" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-400" htmlFor="confirmarPassword">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                    <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" stroke="currentColor" strokeWidth="1.8">
                      <rect x="5" y="11" width="14" height="9" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V8a4 4 0 0 1 8 0v3" />
                    </svg>
                  </span>
                  <input
                    type={mostrarPassword ? 'text' : 'password'}
                    placeholder="Repite la contraseña"
                    className="w-full rounded-lg border border-gray-700 bg-gray-800/80 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-gray-500 transition focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange"
                    id="confirmarPassword"
                    value={confirmarPassword}
                    onChange={(e) => setConfirmarPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={enviando}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-orange py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {enviando && (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z" />
                  </svg>
                )}
                {enviando ? 'Guardando...' : 'Restablecer contraseña'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-400">
            <Link to="/iniciar-sesion" className="font-medium text-brand-orange hover:underline">
              Volver a iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestablecerPassword;
